import React, { useState, useEffect } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import api, { apiError } from "../api";
import { T, primaryBtn, labelStyle, PLAN_LIMITS, PLAN_LABELS, fmtDateIT } from "../theme";
import { StatCard, PasswordField, LogoMark } from "./shared";

function BackBtn({ onBack }) {
  return (
    <button data-testid="panel-back-btn" onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: T.textDim, fontSize: 13, cursor: "pointer", padding: 0 }}>
      <ArrowLeft size={14} /> Torna alla dashboard
    </button>
  );
}

export function GuidePanel({ onBack }) {
  const items = [
    ["Perché So.Help", "La maggior parte degli strumenti per social presuppone che tu sappia già cosa pubblicare, e si limita ad aiutarti a programmarlo o misurarlo. So.Help parte un passo prima: V.I.K.I. scrive la strategia, le didascalie e sceglie le campagne guardando i tuoi dati reali, non un consiglio uguale per tutti. Un limite reale, per onestà: oggi So.Help collega solo Instagram (e la Pagina Facebook a esso collegata, come previsto dal sistema Meta). Facebook più esteso e TikTok sono nella nostra roadmap: abbiamo scelto di fare bene una cosa prima di allargarci a tutte le piattaforme."],
    ["Configurazione guidata", "Il banner \"Iniziamo insieme?\" nella schermata Oggi (o il pulsante nella sidebar) ti porta in pochi passaggi da zero a strategia pronta, calendario impostato e Instagram collegato — incluso, se non hai ancora un profilo Instagram, come crearne uno."],
    ["Cerca ovunque", "Il pulsante \"Cerca\" in alto (o la scorciatoia Ctrl/Cmd+K da tastiera) apre una ricerca rapida per saltare a qualsiasi scheda, profilo o azione senza girare tra i menu."],
    ["Oggi", "La schermata che vedi appena entri. V.I.K.I. ti mostra una sola azione da fare e un'idea di contenuto pronta, così non devi decidere da dove iniziare. Se un suggerimento non fa per te, tocca \"Suggeriscine un'altra\": V.I.K.I. ne terrà conto anche nei prossimi giorni."],
    ["Contenuti", "Scegli un tipo di contenuto, prendi un'idea pronta e assegnala a un giorno della settimana — oppure tocca \"Compila la settimana per me\" e lascia scegliere a V.I.K.I. \"Scrivi tutte le didascalie\" genera in un colpo solo tutte quelle mancanti. Quando un contenuto è approvato e Instagram è collegato, compare il pulsante \"Pubblica ora\"."],
    ["Crescita → Il mio piano", "In cima trovi il pulsante per farti scrivere da V.I.K.I. una strategia su misura per le prossime due settimane, basata sui dati reali del profilo. Sotto, le 5 azioni più efficaci per il tuo obiettivo; più giù tutte le altre tecniche, se vuoi approfondire."],
    ["Crescita → Campagne", "Nella stessa scheda, il pulsante \"Campagne\" in alto mostra modelli pronti di più giorni. Quello con l'etichetta \"Consigliata per te\" è scelto in base al tuo obiettivo. Tocca il titolo per vedere i dettagli, un clic per applicarla al calendario."],
    ["Andamento", "Registra ogni tanto follower ed engagement, oppure sincronizzali da Instagram con un tocco: il grafico ti mostra se la crescita sta funzionando nel tempo."],
    ["Profilo → Cliente ideale", "Descrivi chi vuoi raggiungere: aiuta a scegliere contenuti e tono più adatti a quella persona."],
    ["Profilo → Link in bio", "Nella stessa scheda, il pulsante \"Link in bio\" in alto crea una mini pagina con tutti i tuoi link da mettere nella bio di Instagram, e la scarica pronta da pubblicare."],
    ["Didascalie V.I.K.I.", "Accanto a ogni idea di contenuto puoi chiedere a V.I.K.I. di scrivere una didascalia pronta, con hashtag di nicchia inclusi."],
    ["Instagram e stato dei contenuti", "Dalla scheda Contenuti puoi collegare Instagram (in questa versione, in modo simulato) e seguire lo stato di ogni idea: bozza, approvata, pubblicata."],
    ["Confronto con un concorrente", "In Andamento puoi aggiungere i follower di un profilo simile al tuo e vedere se stai crescendo più o meno velocemente."],
    ["Il tuo engagement rispetto al settore", "Sempre in Andamento, un'etichetta ti dice se il tuo tasso di interazione è nella media per la tua fascia di follower — più il profilo cresce, più il tasso scende naturalmente, quindi il confronto è tarato sulla tua dimensione, non generico."],
    ["Se continui con questo ritmo", "Ancora in Andamento: una stima di quanti follower avrai tra 30 e 90 giorni, calcolata sul tuo trend reale — non una promessa, ma un modo concreto per vedere l'effetto della costanza nel tempo."],
    ["Impostazioni account", "Cambia la password, scegli il piano attivo (Free, Pro o Agency) e le notifiche via email: ogni piano ha un numero massimo di profili gestibili."],
    ["Report cliente", "Genera un riepilogo pulito, da stampare o salvare come PDF: utile se segui un cliente e vuoi mostrargli i risultati."],
    ["Punteggio profilo", "Un numero da 0 a 100 che riassume quanto stai seguendo il piano consigliato, se hai dati registrati e se hai definito un cliente ideale."],
  ];
  return (
    <div data-testid="guide-panel">
      <div style={{ marginBottom: 18 }}><BackBtn onBack={onBack} /></div>
      <div style={{ fontFamily: T.fontHead, fontSize: 22, marginBottom: 6 }}>Come funziona So.Help</div>
      <div style={{ color: T.textDim, fontSize: 13, marginBottom: 20, maxWidth: 560 }}>Una spiegazione breve per ogni sezione dell'app.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 620 }}>
        {items.map(([title, text]) => (
          <div key={title} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 6 }}>{title}</div>
            <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5 }}>{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReportPanel({ profile, personas, score, onBack }) {
  const [bestDays, setBestDays] = useState(null);
  useEffect(() => {
    api.get(`/profiles/${profile.id}/best-days`).then(({ data }) => setBestDays(data)).catch(() => {});
  }, [profile.id]);
  const history = profile.history;
  const current = history[history.length - 1]?.followers || 0;
  const previous = history[history.length - 2]?.followers || current;
  const growthPct = previous ? (((current - previous) / previous) * 100).toFixed(1) : "0.0";
  const doneTasks = Object.entries(profile.tasks || {}).filter(([, v]) => v);
  const persona = personas[0];
  const todayStr = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  const allEntries = Object.values(profile.calendar || {}).flat();
  const approvati = allEntries.filter((e) => e.status === "approvato" || e.status === "programmato").length;
  const programmati = allEntries.filter((e) => e.status === "programmato").length;

  return (
    <div data-testid="report-panel">
      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <BackBtn onBack={onBack} />
        <button data-testid="report-print-btn" onClick={() => window.print()} style={{ ...primaryBtn, display: "flex", alignItems: "center", gap: 6 }}>
          <Printer size={15} /> Stampa / Salva come PDF
        </button>
      </div>

      <div className="print-area" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, padding: 32, maxWidth: 680 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <LogoMark size={20} />
          <span style={{ fontFamily: T.fontHead, fontSize: 16 }}>So.Help</span>
        </div>
        <div style={{ fontFamily: T.fontHead, fontSize: 24, marginTop: 14 }}>Report di crescita — {profile.name}</div>
        <div style={{ color: T.textDim, fontSize: 13, marginBottom: 24 }}>{profile.niche} · Obiettivo: {profile.goal} · Generato il {todayStr}</div>

        <div className="grid-3" style={{ marginBottom: 24 }}>
          <StatCard label="Follower attuali" value={current.toLocaleString("it-IT")} sub={`${growthPct >= 0 ? "+" : ""}${growthPct}% di recente`} accent={growthPct >= 0 ? T.teal : T.rose} />
          <StatCard label="Punteggio profilo" value={score} sub="su 100" />
          <StatCard label="Azioni completate" value={doneTasks.length} sub="dal piano di crescita" />
        </div>

        {history.length > 1 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 10 }}>Andamento follower</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                <XAxis dataKey="date" stroke={T.textDim} fontSize={12} tickFormatter={fmtDateIT} />
                <YAxis stroke={T.textDim} fontSize={12} />
                <Line type="monotone" dataKey="followers" stroke={T.amber} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {bestDays && (
          <div data-testid="report-best-days" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 8 }}>Giorni migliori per pubblicare</div>
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              <b>{bestDays.best_days.join(" e ")}</b>
              {bestDays.mode === "personale" && <span style={{ marginLeft: 8, fontSize: 10, fontFamily: T.fontHead, color: T.ink, background: T.viki, borderRadius: 20, padding: "2px 8px", verticalAlign: "middle" }}>Calcolo personale</span>}
            </div>
            <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>
              {bestDays.mode === "personale"
                ? `Calcolati sui risultati reali di questo profilo (${bestDays.measured_publications} pubblicazioni misurate): in questi giorni le pubblicazioni hanno portato più crescita.`
                : "Media generale di settore: i dati personali di questo profilo non sono ancora sufficienti per un calcolo su misura."}
            </div>
          </div>
        )}

        {persona && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 8 }}>Cliente ideale di riferimento</div>
            <div style={{ fontSize: 14 }}>{persona.nome} — {persona.ruolo}, {persona.settore}</div>
          </div>
        )}

        {allEntries.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 8 }}>Contenuti pianificati questa settimana</div>
            <div style={{ fontSize: 14 }}>{allEntries.length} contenuti in calendario · {approvati} approvati · {programmati} già programmati</div>
          </div>
        )}

        <div style={{ fontSize: 11, color: T.textDim, borderTop: `1px solid ${T.line}`, paddingTop: 12, marginTop: 24 }}>
          Report generato con So.Help il {todayStr}.
        </div>
      </div>
    </div>
  );
}

export function SettingsPanel({ user, profilesCount, onBack, onChangePlan }) {
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdMsg, setPwdMsg] = useState({ type: "", text: "" });
  const [notif, setNotif] = useState(null);
  const [notifSaved, setNotifSaved] = useState(false);
  const plan = user.plan;

  useEffect(() => {
    api.get("/notification-settings").then(({ data }) => setNotif(data)).catch(() => {});
  }, []);

  async function changePassword() {
    setPwdMsg({ type: "", text: "" });
    if (pwdForm.next.length < 8) { setPwdMsg({ type: "error", text: "La nuova password deve avere almeno 8 caratteri." }); return; }
    if (pwdForm.next !== pwdForm.confirm) { setPwdMsg({ type: "error", text: "Le due password non coincidono." }); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwdForm.current, new_password: pwdForm.next });
      setPwdForm({ current: "", next: "", confirm: "" });
      setPwdMsg({ type: "success", text: "Password aggiornata." });
    } catch (e) {
      setPwdMsg({ type: "error", text: apiError(e) });
    }
  }

  async function saveNotif(next) {
    setNotif(next);
    await api.post("/notification-settings", next);
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 1800);
  }

  const plans = [
    { id: "free", name: "Free", desc: "Per iniziare senza rischi", features: ["1 profilo", "Piano di crescita guidato", "Calendario e campagne"] },
    { id: "pro", name: "Pro", desc: "Per un'attività che cresce", features: ["Fino a 5 profili", "Didascalie generate da V.I.K.I.", "Report cliente e link in bio"] },
    { id: "agency", name: "Agency", desc: "Per chi segue più clienti", features: ["Profili illimitati", "Duplicazione profilo", "Flusso di approvazione cliente"] },
  ];

  return (
    <div data-testid="settings-panel">
      <div style={{ marginBottom: 18 }}><BackBtn onBack={onBack} /></div>
      <div style={{ fontFamily: T.fontHead, fontSize: 22, marginBottom: 24 }}>Impostazioni account</div>

      <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 20, marginBottom: 20, maxWidth: 420 }}>
        <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 4 }}>Cambia password</div>
        <div style={{ fontSize: 12, color: T.textDim, marginBottom: 14 }}>{user.email}</div>
        <label style={labelStyle}>Password attuale</label>
        <div style={{ marginBottom: 10 }}><PasswordField testId="settings-current-password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} placeholder="••••••••" /></div>
        <label style={labelStyle}>Nuova password</label>
        <div style={{ marginBottom: 10 }}><PasswordField testId="settings-new-password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} placeholder="••••••••" /></div>
        <label style={labelStyle}>Conferma nuova password</label>
        <div style={{ marginBottom: 14 }}><PasswordField testId="settings-confirm-password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} placeholder="••••••••" /></div>
        {pwdMsg.text && <div data-testid="settings-password-msg" style={{ fontSize: 13, marginBottom: 12, color: pwdMsg.type === "error" ? T.rose : T.teal }}>{pwdMsg.text}</div>}
        <button data-testid="settings-change-password-btn" onClick={changePassword} style={primaryBtn}>Aggiorna password</button>
      </div>

      <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 4 }}>Piano attivo</div>
      <div data-testid="settings-plan-usage" style={{ fontSize: 12, color: T.textDim, marginBottom: 16 }}>Stai usando {profilesCount} profil{profilesCount === 1 ? "o" : "i"} su un massimo di {PLAN_LIMITS[plan] === Infinity ? "illimitati" : PLAN_LIMITS[plan]} per il piano {PLAN_LABELS[plan]}.</div>
      <div className="grid-3" style={{ maxWidth: 720, marginBottom: 8 }}>
        {plans.map((p) => (
          <div key={p.id} data-testid={`settings-plan-${p.id}`} style={{ background: p.id === plan ? T.surfaceAlt : T.surface, border: `1px solid ${p.id === plan ? T.amber : T.line}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 4 }}>{p.name}</div>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 14 }}>{p.desc}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {p.features.map((f) => <div key={f} style={{ fontSize: 12, color: T.textDim }}>— {f}</div>)}
            </div>
            {p.id === plan ? (
              <div style={{ fontSize: 12, color: T.amber, fontWeight: 600 }}>Piano attuale</div>
            ) : (
              <button data-testid={`settings-choose-plan-${p.id}`} onClick={() => onChangePlan(p.id)} style={{ ...primaryBtn, width: "100%" }}>Passa a questo piano</button>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: T.textDim, marginBottom: 28, maxWidth: 500 }}>
        In questa versione il cambio piano è immediato e non attiva alcun pagamento reale. La fatturazione verrà collegata in una fase successiva.
      </div>

      {notif && (
        <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 20, maxWidth: 480 }}>
          <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 4 }}>Notifiche via email {notifSaved && <span style={{ fontSize: 12, color: T.teal, fontFamily: T.font }}>· salvato</span>}</div>
          <div style={{ fontSize: 12, color: T.textDim, marginBottom: 14 }}>Scegli cosa ricevere all'indirizzo {(notif.notify_emails || [user.email]).join(", ")}.</div>
          {[["notify_weekly_report", "Riepilogo settimanale dell'andamento dei profili"],
            ["notify_inactivity", "Avviso se un profilo resta fermo per più di 7 giorni"],
            ["notify_backup", "Conferma di backup completato"]].map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, marginBottom: 10, cursor: "pointer" }}>
              <input data-testid={`settings-${key}`} type="checkbox" checked={!!notif[key]} onChange={(e) => saveNotif({ ...notif, [key]: e.target.checked })} />
              {label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
